import React, { useContext, useEffect, useState } from 'react';
import { Box, Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import classNames from 'classnames';
import { ContentSchema, UserSchema } from '../../../../definitions/schemas';
import { ContentTrackerProps } from '../../../Rooms/Tracking/types';
import { getContentCover, getTextContentURL } from '../../../Content/helper';
import { Properties } from '../../../../enums/Analytics';
import { RoomViewContext } from '../../View/RoomViewContext';
import { UserActivityEvent } from '../../../../enums/UserActivity';
import { VideoPlayerProps } from '../../../VideoPlayer';
import ActivityTracker from '../../../../Services/Analytics/ActivityTracker';
import AudioTracker from '../../../Rooms/Tracking/AudioTracker';
import ContactDetails from '../../../Contact/ContactDetails';
import ContentCover from '../../../Content/ContentCover';
import CoverTile, { CoverTileProps } from '../../../CoverTile';
import DocumentTracker from '../../../Rooms/Tracking/DocumentTracker';
import DownloadTracker from '../../../../Services/Analytics/DownloadTracker';
import ImageTracker from '../../../Rooms/Tracking/ImageTracker';
import ContentType from '../../../../enums/ContentType';
import TextTracker from '../../../Rooms/Tracking/TextTracker';
import VideoTracker from '../../../Rooms/Tracking/VideoTracker';

const useStyles = makeStyles((theme: Theme) => ({
  audioWelcomeMessage: {
    borderRadius: theme.spacing(1),
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
    marginTop: theme.spacing(3),
    minWidth: theme.spacing(30),
    maxWidth: '100%',
  },
  // Force the play button to be positioned in the bottom-right corner
  // to avoid covering a recorded face centered in the video
  playButton: {
    alignItems: 'flex-end',
    justifyContent: 'flex-end',
    padding: theme.spacing(2),
  },
  ownerAvatar: {
    backgroundColor: `${theme.palette.secondary.main}26`,
    color: theme.palette.secondary.main,
  },
  textWelcomeMessage: {
    background: 'var(--background-box-dark)',
    borderRadius: theme.spacing(1),
    fontWeight: 500,
    fontSize: theme.spacing(2.25),
    lineHeight: theme.spacing(3.5),
  },
  smallPlayButton: {
    padding: theme.spacing(1.5),
  },
  whiteBackground: {
    background: theme.palette.background.paper,
  },
}));

type ClassKey = 'audio' | 'audioTracker' | 'text' | 'textTracker';

export interface WelcomeMessagePlayerProps extends ContentTrackerProps {
  autoPlay?: boolean;
  classes?: Partial<Record<ClassKey, string>>;
  content: Partial<ContentSchema>;
  CoverTileProps?: Partial<CoverTileProps>;
  displayOwner?: boolean;
  fixedVideoHeight?: boolean;
  owner?: UserSchema;
  referrer: string;
  videoClasses?: VideoPlayerProps['classes'];
  whiteBackground?: boolean;
  withVideoCover?: boolean;
}

/**
 * Component mostly used to display a welcome message.
 * RoomContentFacade is used for displaying room contents.
 */
const WelcomeMessagePlayerFacade: React.FC<WelcomeMessagePlayerProps> = (
  props: WelcomeMessagePlayerProps
) => {
  const { isGuestVisitor } = useContext(RoomViewContext);

  const classes = useStyles();

  const {
    autoPlay = false,
    classes: classesProp,
    content,
    CoverTileProps,
    displayOwner,
    fixedVideoHeight = false,
    owner,
    referrer,
    videoClasses,
    whiteBackground = false,
    withVideoCover = false,
    ...contentTrackerProps
  } = props;

  const { libraryVisible, type } = content;
  const [cover] = getContentCover(content);

  const [showVideoCover, setShowVideoCover] = useState(withVideoCover);
  const [viewed, setViewed] = useState(false);

  useEffect(() => {
    setShowVideoCover(withVideoCover);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cover]);

  // Empty fragment is returned for
  // - missing or unknown type
  // - broken media (type and data doesn't match)
  //   @todo server should only return valid references
  let component = <></>;
  if (!type) {
    return component;
  }

  const trackingProperties = {
    [Properties.ENTERED_FROM]: referrer,
    [Properties.CONTENT_TYPE]: type,
    [Properties.SINGLE_USE]: !libraryVisible,
    [Properties.WELCOME_MESSAGE]: true,
  };

  const handleDownload = () => {
    if (content.id) {
      new DownloadTracker(
        content.id,
        props.contactId,
        props.roomId,
        content.version
      ).track(isGuestVisitor);
    }
  };

  const handleView = () => {
    const [, isUrl] = getTextContentURL(content.text?.text);
    // urls will be counted as "viewed" every time the user clicks on them
    if (content.id && (!viewed || isUrl)) {
      // don't track user activity if the visitor is a guest
      if (isGuestVisitor) {
        return;
      }

      ActivityTracker.track(UserActivityEvent.ContentViewed, {
        contentId: content.id,
        version: content.version,
      });

      setViewed(true);
    }
  };

  const renderOwner = (): JSX.Element | undefined => {
    if (!(displayOwner && owner)) {
      return undefined;
    }

    return (
      <Box pb={3} px={2}>
        <ContactDetails
          contact={{ ...owner, jobTitle: owner.member?.jobTitle }}
          ContactAvatarProps={{
            background: undefined,
            className: classes.ownerAvatar,
            displayOneInitialOnly: true,
          }}
          displayJobTitle
        />
      </Box>
    );
  };

  switch (type) {
    case ContentType.AUDIO:
      if (content.audio) {
        const {
          audio: { url },
        } = content;
        component = (
          <Box
            className={classNames(
              classesProp?.audio,
              classes.audioWelcomeMessage,
              classes.whiteBackground
            )}
          >
            <AudioTracker
              autoPlay={autoPlay}
              className={classesProp?.audioTracker}
              onPlay={handleView}
              url={url}
              {...contentTrackerProps}
            />
            {renderOwner()}
          </Box>
        );
      }
      break;
    case ContentType.DOCUMENT:
      if (content.document) {
        const { createdAt, document, processingLog } = content;
        const { downloadable } = document;
        const [cover] = getContentCover(content);

        component = (
          <DocumentTracker
            contentProcessingLog={processingLog}
            cover={cover}
            createdAt={createdAt!}
            document={document}
            downloadable={downloadable}
            onDownload={handleDownload}
            onLoad={handleView}
            trackingProperties={trackingProperties}
            {...contentTrackerProps}
          />
        );
      }
      break;
    case ContentType.IMAGE:
      if (content.image) {
        const {
          image: {
            downloadable,
            height,
            predominantColor,
            signedUrl,
            width: imageWidth,
          },
          name,
        } = content;
        component = (
          <ImageTracker
            backgroundColor={predominantColor}
            downloadable={downloadable}
            height={height}
            name={name}
            onDownload={handleDownload}
            onLoad={handleView}
            trackingProperties={trackingProperties}
            url={signedUrl}
            width={imageWidth}
            {...contentTrackerProps}
          />
        );
      }
      break;
    case ContentType.TEXT:
      if (content.text) {
        const {
          text: { text, html },
        } = content;
        component = (
          <Box
            className={classNames(
              classesProp?.text,
              classes.textWelcomeMessage,
              {
                [classes.whiteBackground]: whiteBackground,
              }
            )}
          >
            <TextTracker
              className={classesProp?.textTracker}
              cover={cover}
              html={html}
              onView={handleView}
              text={text}
              {...contentTrackerProps}
            />
            {renderOwner()}
          </Box>
        );
      }
      break;
    case ContentType.VIDEO:
      if (content.video) {
        const {
          video: { captions, downloadUrl, url },
        } = content;
        component = showVideoCover ? (
          <CoverTile {...CoverTileProps}>
            {({ height }) => (
              <Box
                className={videoClasses?.video}
                height={height}
                overflow="hidden"
                position="relative"
                width="100%"
              >
                <ContentCover
                  className={classes.playButton}
                  classNamePlayButton={classes.smallPlayButton}
                  clickable
                  cover={cover}
                  onClick={() => setShowVideoCover(false)}
                  playButton
                />
              </Box>
            )}
          </CoverTile>
        ) : (
          <VideoTracker
            autoPlay={autoPlay || (withVideoCover && !showVideoCover)}
            classes={videoClasses}
            cover={cover}
            downloadUrl={downloadUrl}
            fixedHeight={fixedVideoHeight}
            onPlay={handleView}
            captions={captions}
            url={url}
            {...contentTrackerProps}
          />
        );
      }
      break;
  }
  return component;
};

export default WelcomeMessagePlayerFacade;
